import React, { useCallback, useEffect, useState } from "react";

import { faEdit, faExternalLink } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faAngleDown, faAngleRight } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { compact } from "lodash";
import { Node as ProseMirrorNode } from "prosemirror-model";
import { Decoration, EditorView } from "prosemirror-view";

import { billingUpgradeIconDef } from "~/billing/utils";
import { BlockActionMenu, getUser } from "~/lib/prose/blockActions";
import { makeSpace } from "~/lib/prose/commands";
import { NodeExtension } from "~/lib/prose/extension";
import { blockActionsEnabled } from "~/lib/prose/plugins/blockActions";
import { getContext } from "~/lib/prose/plugins/context";
import {
	type EmbedOption,
	getEmbedObject,
	getSpotifyTypeAndId,
	launchEmbedModal,
} from "~/lib/prose/plugins/integrations/helpers";
import { promptResponseAttrs } from "~/lib/prose/plugins/prompt";
import ReactNodeView, { type ReactNodeViewProps } from "~/lib/prose/react-node-view";
import type { getPosHandler } from "~/lib/prose/types";
import { eventMatchTagsOrClasses } from "~/lib/prose/utils";
import store from "~/lib/store";
import { inVideocallMode, isDesktopApp, isMobile, isNative, isTablet, isZoom } from "~/lib/utils";
import {
	getCommentMenuItem,
	getCopyLinkMenuItem,
	getCreatorMenuItem,
	getDeleteBlockMenu,
	getReactionMenuItem,
} from "~/notes/AssignableDropdown";
import { Tooltip } from "~/ui";
import { Link } from "~/ui/legacyFellowIndex";

import styles from "./embed.module.css";

const formatIdForStorage = (id: string) => `stream.embeds.${id}`;

/**
 * Saves a value to local storage if the embed is collapsed.
 * Removes the value if opened.
 * @param embedBlockId The ID of the embed
 * @param isCollapsed Is the embed closed?
 */
export const saveIsCollapsed = (embedBlockId: string, isCollapsed: boolean) => {
	if (isCollapsed) {
		// `true` is an arbitrary value
		return store.set(formatIdForStorage(embedBlockId), true);
	} else {
		return store.remove(formatIdForStorage(embedBlockId));
	}
};

export const retrieveIsCollapsed = (embedBlockId: string): boolean => {
	return !!store.get(formatIdForStorage(embedBlockId));
};

const getEmbedTitle = (service: EmbedOption, title?: string) => {
	return title ? title : getEmbedObject(service).name;
};

const FrameComponent: React.FC<ReactNodeViewProps> = ({ node }) => {
	const { blockId, title, embedUrl, originalUrl } = node.attrs;
	const service = node.attrs.service as EmbedOption;
	const embedTitle = getEmbedTitle(service, title);

	// We need to determine if the node will be a spotify podcast as they render differently
	const spotifyType = service === "spotify" ? getSpotifyTypeAndId(embedUrl)?.type : null;

	const [isCollapsed, setCollapsed] = useState(true);

	useEffect(() => {
		setCollapsed(retrieveIsCollapsed(blockId));
	}, [blockId]);

	const onClick = useCallback(() => {
		const newState = !isCollapsed;
		setCollapsed(newState);
		saveIsCollapsed(blockId, newState);
	}, [blockId, isCollapsed]);

	const embedInner =
		service === "giphy" ? (
			<img src={embedUrl} alt="embedded gif" />
		) : (
			<iframe
				loading="lazy"
				frameBorder={0}
				scrolling="no"
				allowFullScreen={true}
				width="100%"
				height="100%"
				src={embedUrl}
				title={embedTitle}
			/>
		);

	const customTooltipMessages = {
		figma: {
			prompt: "Why am I asked to log in?",
			message:
				// eslint-disable-next-line max-len
				'The content is likely private. Change the permission settings to "Anyone with the link can view" from Figma, then refresh the page.',
		},
		miro: {
			prompt: "Is this board private?",
			message:
				'From the Miro app, press "Share" and change "Anyone with the link" to "can view", then refresh the page.',
		},
	} as const;

	const tooltipMessage: { prompt: string; message: string } = Object.prototype.hasOwnProperty.call(
		customTooltipMessages,
		service,
	)
		? customTooltipMessages[service as keyof typeof customTooltipMessages]
		: {
				prompt: "Permission issues?",
				message: `Having permission issues? \n\
Try enabling “public link” access from ${getEmbedTitle(service)}, or open in browser. \n\
Click to learn more.`,
			};

	return (
		<div pmcontent="" className={styles.embedContent}>
			<div contentEditable={false} className={styles.menuBar} data-embed-menu-bar>
				<div className={styles.menuGroup}>
					{getEmbedObject(service).icon}
					<Link
						href={originalUrl}
						target="_blank"
						rel="noopener noreferrer"
						className={styles.embedTitle}
						title={embedTitle}
					>
						{embedTitle}
					</Link>
					<div className={styles.printBar}>
						<p className={styles.printTitle}>{embedTitle}</p>
						<a href={originalUrl} rel="noopener noreferrer" target="_blank" className={styles.printUrl}>
							{originalUrl}
						</a>
					</div>
					<button onClick={onClick} className={styles.collapseButton}>
						<FontAwesomeIcon icon={isCollapsed ? faAngleRight : faAngleDown} />
					</button>
				</div>
				{isDesktopApp && (
					<Tooltip
						placement="top"
						maxWidth={220}
						content={<div style={{ textAlign: "center" }}>{tooltipMessage.message}</div>}
					>
						<a
							target="_black"
							rel="noopener noreferrer"
							href="https://help.fellow.app/en/articles/6915767-troubleshooting-embedded-content"
							className={styles.notice}
						>
							{tooltipMessage.prompt}
						</a>
					</Tooltip>
				)}
			</div>
			{!isCollapsed && (
				<div
					className={cx(styles[`iframe-container-${service}`], {
						[styles["iframe-container-spotify-podcast"]]: spotifyType === "show" || spotifyType === "episode",
					})}
				>
					{embedInner}
				</div>
			)}
		</div>
	);
};

const TextComponent: React.FC<ReactNodeViewProps & { disabled: boolean }> = ({ node }) => {
	const { service, title, originalUrl } = node.attrs;
	const embedTitle = getEmbedTitle(service, title);

	return (
		<div className={cx(styles.embedContent, styles.textEmbed)} contentEditable={false}>
			<div contentEditable={false} className={styles.menuBar} data-embed-menu-bar>
				<div className={styles.menuGroup}>
					{getEmbedObject(service).icon}
					<Link
						href={originalUrl}
						target="_blank"
						rel="noopener noreferrer"
						className={styles.embedTitle}
						title={embedTitle}
					>
						{embedTitle}
					</Link>
				</div>
			</div>
		</div>
	);
};

const EmbedComponent: React.FC<ReactNodeViewProps> = props => {
	const { options } = props.view.extensions.get("embed") as Embed;
	const reasonsToHideFrame: Array<boolean> = [
		!options.renderFrame,
		isTablet,
		isMobile,
		isNative,
		isZoom,
		inVideocallMode,
		window.innerWidth <= 735,
	];

	if (reasonsToHideFrame.some(reason => reason)) {
		return <TextComponent disabled={isZoom} {...props} />;
	}

	return <FrameComponent {...props} />;
};

function getExternalLinkMenuItem({ view, getPos }: { view: EditorView; getPos: () => number }) {
	return {
		id: "externalLink",
		icon: faExternalLink,
		right: "Open in new tab",
		action: () => {
			const node = view.state.doc.resolve(getPos()).nodeAfter;
			if (!node) return false;

			window.open(node.attrs.originalUrl, "_blank", "noopener noreferrer");
			return true;
		},
	};
}

function getEditEmbedMenuItem({
	view,
	node,
	getPos,
}: {
	view: EditorView;
	node: ProseMirrorNode;
	getPos: () => number;
}) {
	const hasAccessToEmbeds = getContext(view.state)?.billing?.hasAccessToEmbeds;
	const isLoom = node.attrs.service === "loom";
	const context = getContext(view.state);

	if (context.locked) {
		return null;
	}

	return {
		id: "editEmbed",
		icon: faEdit,
		right: (
			<div className={styles.menuItemWithCrown}>
				<span>Edit media</span>
				{!hasAccessToEmbeds && !isLoom && <FontAwesomeIcon icon={billingUpgradeIconDef} />}
			</div>
		),
		action: () => {
			if (!view.editable) return false;

			launchEmbedModal(view, {
				nodeToReplace: { blockId: node.attrs.blockId, getPos },
				service: node.attrs.service,
			});

			return true;
		},
		disabled: !view.editable,
	};
}

class EmbedView extends ReactNodeView {
	renderComponent(onRender?: () => void) {
		super.renderComponent(() => {
			onRender?.();
			// this function may get called inline in the constructor, through a chain of callbacks,
			// which will cause this._blockActions to be reset to undefined.
			// using setTimeout here to make sure it's not being called inline.
			setTimeout(this.mountBlockActions.bind(this), 0);
		});
	}

	private mountBlockActions() {
		if (!blockActionsEnabled(this.view.state)) return;
		this.dom.appendChild(this.getBlockActions().el);
	}

	private _blockActions?: BlockActionMenu;
	private getBlockActions() {
		if (this._blockActions) return this._blockActions;
		this._blockActions = new BlockActionMenu({
			node: this.node,
			view: this.view,
			menuSections: () => {
				const { node, view, getPos } = this;
				const creatorId = node.attrs.creator;
				const context = getContext(view.state);
				const creator = context?.participants.find((p: { id: string }) => p.id === creatorId);

				if (typeof getPos === "boolean") throw new Error("boolean getPos for node");

				const editEmbedMenuItem = getEditEmbedMenuItem({ view, node, getPos });

				return [
					{ children: compact([getExternalLinkMenuItem({ view, getPos }), editEmbedMenuItem]) },
					{
						children: context.isMeetingModal
							? [getReactionMenuItem({ blockKey: node.attrs.blockId, editorView: view })]
							: !context.isPrivateNote
								? [
										getReactionMenuItem({ blockKey: node.attrs.blockId, editorView: view }),
										getCommentMenuItem({ view, blockKey: node.attrs.blockId }),
									]
								: [],
					},
					{
						children: [
							...(context?.noteId
								? [getCopyLinkMenuItem({ noteId: context.noteId, blockKey: node.attrs.blockId })]
								: []),
							getDeleteBlockMenu({
								view,
								getPos,
								text: "Delete",
								callback: () => store.remove(`stream.embeds.${node.attrs.blockId}`),
							}),
						],
					},
					{ children: !context?.isPrivateNote ? [getCreatorMenuItem({ user: getUser(), creator })] : [] },
				];
			},
			triggers: [],
		});
		return this._blockActions;
	}

	getContainer() {
		const embedWrapper = document.createElement("div");
		embedWrapper.setAttribute("data-block-id", this.attrs.blockId);
		embedWrapper.setAttribute("pmblock", "");
		embedWrapper.className = styles.embedWrapper;
		embedWrapper.contentEditable = "false";

		return embedWrapper;
	}

	getComponent() {
		return EmbedComponent;
	}

	ignoreMutation() {
		return true;
	}

	selectNode() {
		/**
		 * The default behaviour for this function applies an outline to the node and makes it draggable
		 * By defining the function and doing nothing, we eliminate this behaviour, and therefore the outline
		 */
		return;
	}

	deselectNode() {
		return;
	}

	/**
	 * Prose's default behaviour is to select a leaf node when you click it. We don't want to change the selection when
	 * clicking the triple-dot menu however, so this is to prevent that.
	 */
	stopEvent(event: Event) {
		const invalidTags = ["button", "svg", "path"];
		const invalidClasses = ["blockActions", "assignableDropdown"];
		return eventMatchTagsOrClasses(event, invalidTags, invalidClasses);
	}
}

export default class Embed extends NodeExtension {
	constructor(options: { renderFrame: boolean } = { renderFrame: false }) {
		super(options);
	}

	get name() {
		return "embed";
	}

	view = (node: ProseMirrorNode, view: EditorView, getPos: getPosHandler, decorations: readonly Decoration[]) => {
		return new EmbedView(node, view, getPos, decorations);
	};

	get node(): NodeExtension["node"] {
		return {
			group: "block",
			attrs: {
				blockId: { default: null },
				assignees: { default: [] },
				creator: { default: null },
				service: {},
				title: { default: null },
				embedUrl: {},
				embedId: { default: null },
				originalUrl: {},
				...promptResponseAttrs,
			},
			defining: true,
			inline: false,
			atom: true,
			selectable: true,
			toDOM: node => [
				"div",
				{
					"data-block-type": this.name,
					"data-block-id": node.attrs.blockId,
					"data-block-service": node.attrs.service,
					"data-block-title": node.attrs.title,
					"data-block-embedUrl": node.attrs.embedUrl,
					"data-block-embedId": node.attrs.embedId,
					"data-block-originalUrl": node.attrs.originalUrl,
				},
			],
			parseDOM: [
				{
					tag: `[data-block-type="${this.name}"]`,
					getAttrs: dom => {
						if (!(dom instanceof Element)) return false;

						const requiredAttrs = {
							blockId: dom.getAttribute("data-block-id"),
							service: dom.getAttribute("data-block-service"),
							embedUrl: dom.getAttribute("data-block-embedUrl"),
							originalUrl: dom.getAttribute("data-block-originalUrl"),
						};

						// Make sure all required values are present
						if (Object.values(requiredAttrs).some(attr => !attr)) return false;

						return {
							...requiredAttrs,
							title: dom.getAttribute("data-block-title"),
							embedId: dom.getAttribute("data-block-embedId"),
						};
					},
				},
			],
		};
	}

	keys() {
		return {
			ArrowUp: makeSpace("above"),
			ArrowLeft: makeSpace("above"),
			ArrowDown: makeSpace("below"),
			ArrowRight: makeSpace("below"),
		};
	}
}
